import { useCallback } from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const BASE = import.meta.env.VITE_API_BASE || '/api';

// Collapse any font-size span(s) wrapping the whole text (incl. nested) so that
// re-applying a size replaces it instead of nesting another span.
function stripSizeSpans(text) {
  let t = text.trim(), prev;
  do {
    prev = t;
    const m = t.match(/^<span style="font-size:[^"]*">([\s\S]*)<\/span>$/);
    if (m) t = m[1].trim();
  } while (t !== prev);
  return t;
}

// Collapse background-color span(s) wrapping the whole selection so changing
// or clearing a highlight replaces the previous value instead of nesting it.
function stripHighlightSpans(text) {
  let t = text.trim(), prev;
  do {
    prev = t;
    const m = t.match(/^<span style="background-color:[^"]*">([\s\S]*)<\/span>$/);
    if (m) t = m[1].trim();
  } while (t !== prev);
  return t;
}

const LABEL = {
  fontFamily: 'var(--label)', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase',
  display: 'block', marginBottom: 6, color: 'var(--slate-70)',
};

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: fd,
  });
  if (!res.ok) throw new Error('Upload failed');
  const json = await res.json();
  return json.data?.url || '';
}

// Markdown editor with drag/paste/toolbar image upload.
// Shared by the insight editor and the media long-form editor.
export default function MarkdownField({ value, onChange, label }) {
  const insertImageMarkdown = useCallback(async (file, api) => {
    try {
      const url = await uploadImage(file);
      if (!url) return;
      const md = `![${file.name}](${url})`;
      const sel = api.textArea.selectionStart;
      const before = api.textArea.value.slice(0, sel);
      const after = api.textArea.value.slice(api.textArea.selectionEnd);
      api.replaceSelection(md);
      onChange(before + md + after);
    } catch (e) {
      alert('Image upload failed');
    }
  }, [onChange]);

  const imageUploadCommand = {
    name: 'image-upload',
    keyCommand: 'image-upload',
    buttonProps: { 'aria-label': 'Upload image', title: 'Upload image' },
    icon: (
      <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M3 4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4zm2 0v9.59l3.3-3.3a1 1 0 0 1 1.4 0L13 13.59 14.3 12.3a1 1 0 0 1 1.4 0L17 13.59V4H5zm0 12h10v-.59l-2-2-1.3 1.3a1 1 0 0 1-1.4 0L7 11.41 5 13.41V16zm8-9a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z" />
      </svg>
    ),
    execute: (state, apiCtx) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (file) await insertImageMarkdown(file, apiCtx);
      };
      input.click();
    },
  };

  const handlePaste = (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          event.preventDefault();
          uploadImage(file).then(url => {
            if (url) {
              const ta = event.target;
              const sel = ta.selectionStart;
              const md = `![${file.name || 'image'}](${url})`;
              const next = ta.value.slice(0, sel) + md + ta.value.slice(ta.selectionEnd);
              onChange(next);
            }
          }).catch(() => alert('Image upload failed'));
          return;
        }
      }
    }
  };

  const handleDrop = (event) => {
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      event.preventDefault();
      uploadImage(file).then(url => {
        if (url) {
          const ta = event.target.closest('.w-md-editor')?.querySelector('textarea');
          if (!ta) return;
          const sel = ta.selectionStart;
          const md = `![${file.name || 'image'}](${url})`;
          const next = ta.value.slice(0, sel) + md + ta.value.slice(ta.selectionEnd);
          onChange(next);
        }
      }).catch(() => alert('Image upload failed'));
    }
  };

  // Font-size toolbar. Re-applying a size replaces the current one (no nesting):
  // strip a wrapping size span on the selection, and absorb one immediately
  // enclosing it (when only the inner text is selected). 正常 clears sizing.
  const applySize = (api, wrap) => {
    const ta = api.textArea;
    let s = ta.selectionStart, e = ta.selectionEnd;
    const full = ta.value;
    const open = full.slice(0, s).match(/<span style="font-size:[^"]*">\s*$/);
    const close = full.slice(e).match(/^\s*<\/span>/);
    if (open && close) { s -= open[0].length; e += close[0].length; }
    const inner = stripSizeSpans(full.slice(s, e)) || '文字';
    onChange(full.slice(0, s) + wrap(inner) + full.slice(e));
  };

  const sizeCmd = (label, wrap) => ({
    name: `size-${label}`,
    keyCommand: `size-${label}`,
    buttonProps: { 'aria-label': `Font size ${label}`, title: `字号：${label}` },
    icon: <span style={{ fontSize: 12 }}>{label}</span>,
    execute: (state, api) => applySize(api, wrap),
  });

  const wrapPx = size => inner => `<span style="font-size:${size}">${inner}</span>`;
  const sizeGroup = commands.group(
    [
      sizeCmd('小', wrapPx('0.8em')),
      sizeCmd('正常', inner => inner),
      sizeCmd('大', wrapPx('1.25em')),
      {
        name: 'size-custom', keyCommand: 'size-custom',
        buttonProps: { 'aria-label': 'Custom font size', title: '字号：自定义 px' },
        icon: <span style={{ fontSize: 12 }}>px</span>,
        execute: (state, api) => {
          const n = parseInt(window.prompt('字号（px）', '14'), 10);
          if (n) applySize(api, wrapPx(`${n}px`));
        },
      },
    ],
    {
      name: 'font-size', groupName: 'font-size',
      buttonProps: { 'aria-label': 'Font size', title: '字号' },
      icon: <span style={{ fontSize: 13, fontWeight: 600 }}>A↕</span>,
    },
  );

  // Text-background toolbar. The generated HTML is intentionally limited to
  // background-color on a span; ArticleView's sanitizer already permits span
  // styles, so the same markup works in both the editor preview and public page.
  const applyHighlight = (api, color) => {
    const ta = api.textArea;
    let s = ta.selectionStart, e = ta.selectionEnd;
    const full = ta.value;
    const open = full.slice(0, s).match(/<span style="background-color:[^"]*">\s*$/);
    const close = full.slice(e).match(/^\s*<\/span>/);
    if (open && close) { s -= open[0].length; e += close[0].length; }
    const inner = stripHighlightSpans(full.slice(s, e)) || '文字';
    const next = color
      ? `<span style="background-color:${color};display:block;padding:.35em .55em">${inner}</span>`
      : inner;
    onChange(full.slice(0, s) + next + full.slice(e));
  };

  const swatch = (color, crossed = false) => (
    <span style={{
      display: 'inline-block', width: 14, height: 14,
      border: '1px solid rgba(46,58,69,.28)', borderRadius: 2,
      background: crossed
        ? 'linear-gradient(135deg, transparent 44%, #b44 45%, #b44 55%, transparent 56%)'
        : color,
    }} />
  );

  const highlightCmd = (name, label, color) => ({
    name: `highlight-${name}`,
    keyCommand: `highlight-${name}`,
    buttonProps: { 'aria-label': label, title: label },
    icon: swatch(color),
    execute: (state, api) => applyHighlight(api, color),
  });

  const highlightGroup = commands.group(
    [
      highlightCmd('yellow', '背景色：浅黄', '#FFF2A8'),
      highlightCmd('beige', '背景色：米色', '#E9DDC7'),
      highlightCmd('green', '背景色：浅绿', '#DDEBDD'),
      highlightCmd('blue', '背景色：浅蓝', '#DDE8F3'),
      highlightCmd('rose', '背景色：浅粉', '#F2DDE0'),
      {
        name: 'highlight-custom', keyCommand: 'highlight-custom',
        buttonProps: { 'aria-label': 'Custom background color', title: '背景色：自定义' },
        icon: (
          <span style={{
            display: 'inline-block', width: 14, height: 14, borderRadius: 2,
            border: '1px solid rgba(46,58,69,.28)',
            background: 'linear-gradient(135deg, #f7d36b, #9ed6ba 48%, #9ebce5)',
          }} />
        ),
        execute: (state, api) => {
          const input = document.createElement('input');
          input.type = 'color';
          input.value = '#FFF2A8';
          input.onchange = (event) => applyHighlight(api, event.target.value);
          input.click();
        },
      },
      {
        name: 'highlight-clear', keyCommand: 'highlight-clear',
        buttonProps: { 'aria-label': 'Clear background color', title: '清除背景色' },
        icon: swatch('transparent', true),
        execute: (state, api) => applyHighlight(api, ''),
      },
    ],
    {
      name: 'text-background', groupName: 'text-background',
      buttonProps: { 'aria-label': 'Text background color', title: '文字背景色' },
      icon: (
        <span style={{
          display: 'inline-block', minWidth: 15, padding: '0 2px',
          fontSize: 13, fontWeight: 700, lineHeight: '15px',
          background: '#FFF2A8', color: '#2E3A45',
        }}>A</span>
      ),
    },
  );

  return (
    <div data-color-mode="light">
      {label && <label style={LABEL}>{label}</label>}
      <MDEditor
        value={value || ''}
        onChange={v => onChange(v || '')}
        height={420}
        preview="edit"
        previewOptions={{ remarkPlugins: [remarkGfm], rehypePlugins: [rehypeRaw] }}
        textareaProps={{
          onPaste: handlePaste,
          onDrop: handleDrop,
          placeholder: 'Write in Markdown… paste or drop images to upload.',
        }}
        commands={[
          commands.bold, commands.italic, commands.strikethrough, sizeGroup, highlightGroup,
          commands.divider,
          commands.title2, commands.title3,
          commands.divider,
          commands.unorderedListCommand, commands.orderedListCommand, commands.checkedListCommand,
          commands.divider,
          commands.link, imageUploadCommand, commands.table, commands.quote, commands.code, commands.codeBlock,
          commands.divider,
          commands.hr,
        ]}
        extraCommands={[commands.codeEdit, commands.codeLive, commands.codePreview, commands.fullscreen]}
      />
    </div>
  );
}
